/**
 * Clinity translation dictionary.
 *
 * Authoring rules:
 * - Hindi (hi) and Marathi (mr) are written in *spoken/conversational* register —
 *   the way a friend would explain it, NOT a textbook translation.
 * - English (en) is the canonical source.
 * - Keep keys flat and namespaced (nav.*, hero.*, assess.q.<id>.*, etc.)
 */

const translations = {
  en: {
    // ===================== LANDING (new design) =====================
    'landing.nav.cta': 'Get My Score',
    'landing.hero.trust': 'Doctor-Led • AI-Powered • 2-Minute Assessment',
    'landing.hero.h1.line1': 'You can win in one area',
    'landing.hero.h1.line2': 'but still feel stuck.',
    'landing.hero.synthesis': 'Life Works Better When Everything Works Together',
    'landing.hero.sub': 'You know your age. Do you know your Wellness Score?',
    'landing.hero.supporting': 'Measure where you stand across the dimensions that matter most.',
    'landing.hero.primary': 'Get My Wellness Score',
    'landing.hero.secondary': 'Learn More',
    'landing.outcomes.h2': 'Improved Outcomes Across All Dimensions',
    'landing.outcomes.sub': 'Measure. Understand. Improve.',

    'landing.wheel.eyebrow': 'The Clinity Framework',
    'landing.wheel.h2': 'What Makes Up Your Wellness Score',
    'landing.wheel.sub': 'Your wellbeing isn\'t one number. It\'s a combination of 8 interconnected dimensions.',
    'landing.wheel.hint': 'Hover any segment to see what we measure.',

    'landing.unlock.eyebrow': 'What You Unlock',
    'landing.unlock.h2': 'What You Unlock',
    'landing.unlock.sub': 'Build a wellbeing system that compounds over time.',
    'landing.unlock.clarity': 'Clarity',
    'landing.unlock.balance': 'Balance',
    'landing.unlock.performance': 'Performance',
    'landing.unlock.growth': 'Growth',
    'landing.unlock.resilience': 'Resilience',
    'landing.unlock.fulfillment': 'Fulfillment',
    'landing.unlock.longevity': 'Longevity',

    'landing.builtfor.h2': 'Built For',
    'landing.builtfor.students.title': 'Students',
    'landing.builtfor.students.body': 'Build the foundations of a wellbeing-first life before career pressure arrives.',
    'landing.builtfor.professionals.title': 'Professionals',
    'landing.builtfor.professionals.body': 'Stop trading health, relationships and purpose for promotions you barely enjoy.',
    'landing.builtfor.entrepreneurs.title': 'Entrepreneurs',
    'landing.builtfor.entrepreneurs.body': "Your business won't outperform your wellbeing. Measure both. Optimise both.",
    'landing.builtfor.anyone.title': 'Anyone',
    'landing.builtfor.anyone.body': 'Anyone who wants to live a measured, intentional, complete life.',

    'landing.about.eyebrow': 'About Clinity',
    'landing.about.h2': "Wellbeing isn't a feeling. It's a system.",
    'landing.about.body': 'Clinity is a doctor-led, AI-powered wellbeing operating system. We measure the 8 dimensions of your life, surface what is quietly pulling you back, and prescribe a system you can actually follow.',

    'landing.founder.eyebrow': 'Founder',
    'landing.founder.h2': 'Why I built Clinity',
    'landing.founder.body.p1': 'While pursuing my medical journey, I realized that despite having access to endless information about health, productivity, finance, and personal growth, life still felt fragmented.',
    'landing.founder.body.p2': 'Like many people, I was trying to improve different areas of life without a system to connect them.',
    'landing.founder.body.p3': 'That experience led me to build Clinity.',
    'landing.founder.name': 'Dr. Nishad Shasheen Kulkarni',
    'landing.founder.degree': 'MBBS',
    'landing.founder.role': 'Founder, Clinity',
    'landing.founder.body': "After years of treating patients who were successful on paper but exhausted in life, I realised wellness needed a framework — not another supplement, another app, or another generic tip. Clinity is that framework: one system that connects health, mind, money, career, and purpose into something measurable and improvable.",
    'landing.founder.creds': 'MBBS · Founder, Clinity',

    'landing.problem.h2': "The real problem isn't a lack of information.",
    'landing.problem.sub': 'Most people already know what they should do. The challenge is knowing where to focus.',
    'landing.problem.card.clarity.title': 'Lack of Clarity',
    'landing.problem.card.clarity.quote': "\u201CI don't know what's actually holding me back.\u201D",
    'landing.problem.card.overwhelm.title': 'Overwhelm',
    'landing.problem.card.overwhelm.quote': "\u201CEverything feels important and I don't know where to start.\u201D",
    'landing.problem.card.imbalance.title': 'Imbalance',
    'landing.problem.card.imbalance.quote': "\u201COne area of life improves while another falls apart.\u201D",
    'landing.problem.card.measurement.title': 'No Measurement',
    'landing.problem.card.measurement.quote': "\u201CI have no way to objectively understand my wellbeing.\u201D",

    'landing.root.h2.line1': "You're not missing motivation.",
    'landing.root.h2.line2': "You're missing a system.",
    'landing.root.body': 'Most people manage health, finances, career and relationships separately. But wellbeing is a system. Without a framework, life becomes reactive, fragmented and difficult to navigate.',

    'landing.solution.eyebrow': 'The Clinity Way',
    'landing.solution.h2': 'Meet Clinity',
    'landing.solution.sub': 'Your personal wellbeing operating system.',
    'landing.solution.body': 'Clinity helps you measure the key dimensions of wellbeing, identify hidden gaps, and build a personalized system for long-term growth.',

    'landing.how.h2': 'How It Works',
    'landing.how.step1': 'Take the 2-minute assessment',
    'landing.how.step2': 'Receive your Wellness Score',
    'landing.how.step3': "Discover what's holding you back",
    'landing.how.step4': 'Build your personalized wellbeing system',

    'landing.wheel.eyebrow': 'The Clinity Framework',
    'landing.wheel.h2': 'What Makes Up Your Wellness Score',
    'landing.wheel.body': 'Your wellbeing isn\'t one number. It\'s a combination of 8 interconnected dimensions.',
    'landing.wheel.hint': 'Hover any segment to see what we measure.',

    'landing.final.h2': 'Before you improve your life, measure it.',
    'landing.final.sub': 'Get clarity on where you stand and discover the areas limiting your wellbeing.',
    'landing.final.cta': 'Get My Wellness Score',
    'landing.final.subtext': '2 minutes. Free assessment. Instant insights.',

    // Navbar
    'nav.problem': 'Problem',
    'nav.solution': 'Solution',
    'nav.outcomes': 'Improved Outcomes',
    'nav.founder': 'Founder',
    'nav.cta': 'Start Assessment',

    // Hero
    'hero.title.line1': 'Discover',
    'hero.title.line2': "What's Holding You Back",
    'hero.subtitle': 'Take the Clinity Wellness Assessment and uncover the areas of life that may be limiting your wellbeing.',
    'hero.cta.primary': 'Start Assessment',
    'hero.cta.secondary': 'Learn More',
    'hero.tag': 'Doctor-Led · AI-Powered · 2-Minute Assessment',

    // Problem (broken wires)
    'problem.eyebrow': 'The Problem',
    'problem.title.part1': "The real problem isn't ",
    'problem.title.part2': 'information',
    'problem.caption.bold': 'Endless advice.',
    'problem.caption.rest': 'No framework.',

    // Solution
    'solution.eyebrow': 'The Solution',
    'solution.title.part1': 'From fragmentation to ',
    'solution.title.part2': 'clarity',
    'solution.subtitle': 'Clinity connects the key dimensions of wellbeing into one personalized system.',
    'solution.cta': 'Start Assessment',

    // Journey
    'journey.eyebrow': 'The Clinity Journey',
    'journey.title.part1': 'Three steps to a ',
    'journey.title.part2': 'connected life',
    'journey.step1.title': 'Assessment',
    'journey.step1.desc': 'A 2-minute test across 8 wellness dimensions.',
    'journey.step2.title': 'Clinity Personalized Plan',
    'journey.step2.desc': 'A doctor-led, AI-powered roadmap built around you.',
    'journey.step3.title': 'Improved Outcomes',
    'journey.step3.desc': 'Lasting change you can see, feel, and track.',

    // Outcomes
    'outcomes.eyebrow': 'Improved Outcomes',
    'outcomes.title.part1': 'What you ',
    'outcomes.title.part2': 'unlock',
    'outcomes.clarity': 'Clarity',
    'outcomes.balance': 'Balance',
    'outcomes.performance': 'Performance',
    'outcomes.growth': 'Growth',
    'outcomes.resilience': 'Resilience',
    'outcomes.fulfillment': 'Fulfillment',
    'outcomes.longevity': 'Longevity',

    // Who is it for
    'who.eyebrow': "Who it's for",
    'who.title.part1': 'Built for ',
    'who.title.part2': 'how you actually live',
    'who.students.name': 'Students',
    'who.students.tagline': 'Focus, study balance, growth',
    'who.professionals.name': 'Professionals',
    'who.professionals.tagline': 'Beat burnout. Optimise life.',
    'who.entrepreneurs.name': 'Entrepreneurs',
    'who.entrepreneurs.tagline': 'Build the founder you want to be',
    'who.anyone.name': 'Anyone',
    'who.anyone.tagline': 'Tired of fragmented advice',

    // Founder
    'founder.eyebrow': 'Founder',
    'founder.title.part1': 'Why I built ',
    'founder.title.part2': 'Clinity',
    'founder.quote.p1': 'While pursuing my medical journey, I realized that despite having access to endless information about health, productivity, finance, and personal growth, life still felt fragmented.',
    'founder.quote.p2': 'Like many people, I was trying to improve different areas of life without a system to connect them.',
    'founder.quote.p3': 'That experience led me to build',
    'founder.role': 'Founder, Clinity',

    // Follow + Newsletter
    'follow.eyebrow': 'Stay connected',
    'follow.title.part1': 'Follow the ',
    'follow.title.part2': 'journey',
    'follow.instagram': 'Instagram',
    'follow.instagram.handle': '@clinity',
    'follow.whatsapp': 'WhatsApp',
    'follow.whatsapp.handle': 'Channel',
    'follow.youtube': 'YouTube',
    'follow.youtube.handle': 'Subscribe',
    'newsletter.title': 'Get wellness insights, weekly',
    'newsletter.subtitle': 'Doctor-curated wellbeing essays. No spam. Unsubscribe anytime.',
    'newsletter.placeholder': 'you@example.com',
    'newsletter.cta': 'Subscribe',
    'newsletter.success': "Thanks — you're on the list.",

    // Footer
    'footer.tagline': 'Navigating wellness together',
    'footer.line': 'Create your personal wellbeing system',
    'footer.doctorled': 'Doctor-led',
    'footer.aipowered': 'AI-powered',
    'footer.copyright': '© 2026 Clinity. All rights reserved.',

    // Floating WhatsApp
    'wa.float.title': 'Talk to a Clinity Wellness Expert',
    'wa.float.subtitle': 'Get personalized guidance on WhatsApp — no waiting.',

    // Assessment
    'assess.brand': 'Take Your Wellness Test',
    'assess.step': 'Step {current} of {total}',
    'assess.micro.start': 'Just getting started…',
    'assess.micro.quarter': "You're doing great",
    'assess.micro.half': "You're halfway through",
    'assess.micro.almost': 'Almost done',
    'assess.micro.last': 'Last question',
    'assess.previous': '← Previous Question',
    'assess.continue': 'Continue',
    'assess.section': 'Section {n} of {total}',
    'assess.loading': 'Analysing your responses…',
    'assess.submit.error': 'Submit failed. Your answers are saved locally.',
    'assess.submit.success': 'Assessment Completed Successfully',

    // Dimension labels (display)
    'dim.physical': 'Physical',
    'dim.mental': 'Mental',
    'dim.emotional': 'Emotional',
    'dim.financial': 'Financial',
    'dim.career': 'Career',
    'dim.lifestyle': 'Lifestyle & Environmental',
    'dim.spiritual': 'Spiritual',
    'dim.self_care': 'Self-Care',

    // Dimension intro explanations
    'dim.physical.text': 'Your physical health forms the foundation of your energy, confidence, and long-term performance.',
    'dim.mental.text': 'Your mental clarity and stress control determine how effectively you think, decide, and perform daily.',
    'dim.emotional.text': 'Your emotional stability and relationships shape your happiness, confidence, and inner balance.',
    'dim.financial.text': 'Your financial health directly impacts your security, lifestyle choices, and future freedom.',
    'dim.career.text': 'Your career growth and direction define your purpose, progress, and long-term fulfillment.',
    'dim.lifestyle.text': 'Your daily habits and environment quietly shape your overall life quality and outcomes.',
    'dim.spiritual.text': 'Your sense of purpose and inner peace determines how grounded and fulfilled you feel.',
    'dim.self_care.text': 'Your self-care and appearance reflect how you value yourself and impact your confidence.',

    // Result page
    'result.title.part1': 'Your ',
    'result.title.part2': 'Wellness Test Results',
    'result.score.label': 'Overall Score',
    'result.cta.whatsapp': 'Get My Wellness Prescription on WhatsApp',
    'result.cta.retake': 'Retake Assessment',
    'result.share.greeting': 'Hi Clinity, I have completed my wellness test.',
    'result.share.name': 'Name',
    'result.share.score': 'My Overall Score',
    'result.share.detailed': 'Detailed Scores:',
    'result.share.footer': 'Please share my personalized wellness prescription plan.',

    // Wellness Champion badge system (English)
    'champion.congrats.eyebrow': '🎉 Congratulations',
    'champion.celebration.title': 'You earned the Clinity Wellness Champion',
    'champion.celebration.subtitle': 'Recognized for demonstrating strong wellbeing across multiple dimensions of life.',
    'champion.celebration.description': 'Share your achievement, inspire others, and keep navigating wellness with us.',
    'champion.name.label': 'Add your name to the badge',
    'champion.name.placeholder': 'Enter your name',
    'champion.name.hint': 'Personalizes your badge instantly — leave empty to skip.',
    'champion.share.instagram': 'Instagram',
    'champion.share.whatsapp': 'WhatsApp',
    'champion.share.linkedin': 'LinkedIn',
    'champion.share.download': 'Download',
    'champion.share.downloaded': 'Saved',
    'champion.share.hint': 'Tag #Clinity #WellnessChampion when you share',
    'champion.progress.eyebrow': 'Your Wellness Stage',
    'champion.stage.beginning.title': 'Beginning Your Wellness Journey',
    'champion.stage.beginning.body': 'Every journey starts with awareness. You\'ve taken the most important step — measuring where you are today.',
    'champion.stage.building.title': 'Building Momentum',
    'champion.stage.building.body': 'You\'re developing the foundations of long-term wellbeing. Stay consistent — small steps compound into big shifts.',
    'champion.stage.near.title': 'Near Champion Status',
    'champion.stage.near.body': 'You\'re making strong progress. You\'re only a few steps away from becoming a Clinity Wellness Champion. Continue improving and reassess your progress.',
    'champion.stage.champion.title': 'Clinity Wellness Champion',
    'champion.stage.champion.body': 'Recognized for demonstrating strong wellbeing across multiple dimensions of life. Unlock your shareable Wellness Champion badge.',
    'champion.locked.hint': 'Reach 70+ to unlock the Clinity Wellness Champion badge',

    // Landing-page promo
    'landing.champion.eyebrow': 'Achievement',
    'landing.champion.h2': 'Score Well. Earn Your Shareable Wellness Badge.',
    'landing.champion.body': 'Score 70 or above and unlock your Clinity Wellness Champion badge. Share your achievement and inspire others to take charge of their wellbeing.',
    'landing.champion.cta': 'Take the Assessment',

    // Prescription paywall + flow (English)
    'rx.locked.eyebrow': 'Personalized Wellness Prescription',
    'rx.locked.title': 'Get your free doctor-led prescription',
    'rx.locked.subtitle': 'A personalized wellness prescription built around your assessment — delivered instantly on WhatsApp. Completely free.',
    'rx.locked.bullet.diagnosis': 'Personalized wellness insights across your lowest-scoring dimensions',
    'rx.locked.bullet.rootcause': 'Identify the key areas impacting your overall wellbeing',
    'rx.locked.bullet.systems': 'Practical action plan designed around your assessment results',
    'rx.locked.bullet.medicines': 'Recommended wellness practices and lifestyle improvements',
    'rx.locked.bullet.guidance': 'Guidance across all dimensions of wellbeing',
    'rx.locked.bullet.outcome': '90 & 180-day wellbeing improvement roadmap',
    'rx.locked.cta': 'Get My Free Wellness Prescription',
    'rx.locked.tagline': 'Navigating Wellness Together™',
    'rx.locked.trust': 'Doctor-reviewed · 100% free · Instant WhatsApp delivery',
    'rx.locked.free': 'Free',
    'rx.locked.preview.diagnosis': 'Wellness Insights',
    'rx.locked.preview.rootcause': 'Key Growth Areas',
    'rx.locked.preview.systems': 'Personalized Action Plan',
    'rx.locked.preview.medicines': 'Recommended Practices',

    // Premium / Priority Access section (English)
    'rx.premium.eyebrow': 'Exclusive Access',
    'rx.premium.title': 'Get Priority Access',
    'rx.premium.intro1': 'Not everyone needs more information.',
    'rx.premium.intro2': 'Some people need the right guidance, environment, and system.',
    'rx.premium.body': 'Get priority access to future Clinity wellness experiences designed to improve every dimension of wellbeing.',
    'rx.premium.b1.title': 'Personalized Wellness Consultation',
    'rx.premium.b1.body': 'A tailored wellbeing review based on your Wellness Score.',
    'rx.premium.b2.title': 'Wellness Spaces',
    'rx.premium.b2.body': 'Discover wellness-focused spaces designed to support healthier living, recovery, focus, productivity, and personal growth.',
    'rx.premium.b3.title': 'Wellness Retreats',
    'rx.premium.b3.body': 'Curated retreat recommendations focused on restoration, clarity, balance, performance, wellbeing, and longevity.',
    'rx.premium.b4.title': 'Personalized Wellbeing Roadmap',
    'rx.premium.b4.body': 'Actionable guidance across all dimensions of wellbeing.',
    'rx.premium.b5.title': 'Priority Access To Future Clinity Programs',
    'rx.premium.b5.body': 'Be among the first to access upcoming Clinity experiences, tools, assessments, and premium wellbeing offerings.',
    'rx.premium.cta': 'Get Priority Access',
    'rx.premium.wa_message': "Hi Clinity Team, I would like to get Priority Access to Clinity's premium wellness experiences, wellness spaces, retreats, and future offerings.",
    // Clinity Clinics waitlist hero
    'rx.clinics.eyebrow': 'Coming Soon',
    'rx.clinics.title': 'Clinity Clinics',
    'rx.clinics.subtitle': 'Premium in-person wellness clinics — designed to complete every dimension of your wellbeing.',
    'rx.clinics.cta': 'Join the Waitlist',
    'rx.clinics.note': 'Early access invites go out on WhatsApp',
    'rx.clinics.wa_message': 'Hi Clinity Team, I would like to join the Clinity Clinics waitlist for early access.',
    'rx.contact.title': 'Message us on WhatsApp',
    'rx.contact.subtitle': 'Send this message to our team to claim Priority Access.',
    'rx.contact.number_label': 'WhatsApp number',
    'rx.contact.message_label': 'Pre-filled message',
    'rx.contact.copy': 'Copy',
    'rx.contact.copied': 'Copied',
    'rx.contact.open_wa': 'Open WhatsApp',
    'rx.contact.help': "If WhatsApp doesn't open, copy the number above and message us from your phone.",
    'rx.contact.close': 'Close',

    // Personalization form
    'rx.form.title': "Let's personalize your prescription",
    'rx.form.subtitle': 'A few details — so Dr. Clinity writes for you, not for everyone.',
    'rx.form.name': 'Full name',
    'rx.form.name.placeholder': 'Aanya Sharma',
    'rx.form.age': 'Age',
    'rx.form.age.placeholder': '28',
    'rx.form.gender': 'Gender',
    'rx.form.gender.female': 'Female',
    'rx.form.gender.male': 'Male',
    'rx.form.gender.other': 'Other',
    'rx.form.occupation': 'Occupation',
    'rx.form.occupation.placeholder': 'Product designer',
    'rx.form.city': 'City',
    'rx.form.city.placeholder': 'Mumbai',
    'rx.form.whatsapp': 'WhatsApp number',
    'rx.form.whatsapp.placeholder': '+91 98765 43210',
    'rx.form.email': 'Email (optional)',
    'rx.form.email.placeholder': 'you@example.com',
    'rx.form.email.invalid': 'Please enter a valid email address',
    'rx.form.goal': 'Primary wellness goal',
    'rx.form.goal.energy': 'Build energy & stamina',
    'rx.form.goal.stress': 'Beat stress & overwhelm',
    'rx.form.goal.career': 'Career clarity & growth',
    'rx.form.goal.money': 'Financial foundation',
    'rx.form.goal.purpose': 'Find purpose & meaning',
    'rx.form.goal.selfcare': 'Self-care & confidence',
    'rx.form.goal.overall': 'Overall wellbeing upgrade',
    'rx.form.continue': 'Get my prescription',
    'rx.form.cancel': 'Cancel',
    'rx.form.privacy': 'We use this only to personalize your prescription. Never shared.',
    'rx.form.required': 'Please fill all required fields',

    // Generating loader
    'rx.gen.title': 'Generating your Wellness Prescription…',
    'rx.gen.subtitle': "Dr. Clinity is writing your personalized plan. Just a moment.",
    'rx.gen.step1': 'Analysing your 8 dimensions',
    'rx.gen.step2': 'Writing clinical diagnosis',
    'rx.gen.step3': 'Assigning Clinity Systems™',
    'rx.gen.step4': 'Preparing your PDF + WhatsApp delivery',

    // Ready state
    'rx.ready.title': 'Your Prescription is Ready',
    'rx.ready.subtitle': 'Your personalized Clinity Wellness Prescription has been delivered to WhatsApp.',
    'rx.ready.saved': 'Your report is securely saved and can be accessed anytime.',
    'rx.ready.download': 'Download PDF',
    'rx.ready.whatsapp': 'View My Prescription on WhatsApp',
    'rx.ready.view': 'View My Prescription',
    'rx.ready.retake': 'Retake Assessment',
    'rx.ready.failsafe.title': 'WhatsApp delivery pending',
    'rx.ready.failsafe.body': 'No problem — your prescription is safely saved here:',

    // Errors
    'rx.error.payment': 'Payment failed. Please try again.',
    'rx.error.generation': "We couldn't generate your prescription. Our team has been notified.",
    'rx.error.network': 'Network error. Please try again.',
  },

  hi: {
    // ===================== LANDING (new design) =====================
    'landing.nav.cta': 'मेरा स्कोर पाएं',
    'landing.hero.trust': 'डॉक्टर-नेतृत्व • AI-पावर्ड • 2-मिनट का असेसमेंट',
    'landing.hero.h1.line1': 'आप एक क्षेत्र में जीत सकते हैं',
    'landing.hero.h1.line2': 'फिर भी अटके हुए महसूस कर सकते हैं।',
    'landing.hero.synthesis': 'जब सब कुछ एक साथ काम करता है, तब जीवन बेहतर चलता है।',
    'landing.hero.sub': 'आप अपनी उम्र जानते हैं। क्या आप अपना वेलनेस स्कोर जानते हैं?',
    'landing.hero.supporting': 'जो पहलू सबसे ज़्यादा मायने रखते हैं — उन पर आप कहाँ खड़े हैं, मापिए।',
    'landing.hero.primary': 'मेरा वेलनेस स्कोर पाएं',
    'landing.hero.secondary': 'और जानें',

    'landing.outcomes.h2': 'सभी पहलुओं में बेहतर नतीजे',
    'landing.outcomes.sub': 'मापें। समझें। सुधारें।',

    'landing.wheel.eyebrow': 'Clinity फ्रेमवर्क',
    'landing.wheel.h2': 'आपका वेलनेस स्कोर किन चीज़ों से बनता है',
    'landing.wheel.sub': 'आपकी वेलबीइंग एक संख्या नहीं — यह 8 आपस में जुड़े पहलुओं का जोड़ है।',
    'landing.wheel.hint': 'किसी भी हिस्से पर hover करें।',

    'landing.unlock.eyebrow': 'क्या आप पाएंगे',
    'landing.unlock.h2': 'क्या आप पाएंगे',
    'landing.unlock.sub': 'एक वेलबीइंग सिस्टम जो समय के साथ बढ़ता जाए।',
    'landing.unlock.clarity': 'स्पष्टता',
    'landing.unlock.balance': 'संतुलन',
    'landing.unlock.performance': 'प्रदर्शन',
    'landing.unlock.growth': 'विकास',
    'landing.unlock.resilience': 'दृढ़ता',
    'landing.unlock.fulfillment': 'संतुष्टि',
    'landing.unlock.longevity': 'दीर्घायु',

    'landing.builtfor.h2': 'किसके लिए बना है',
    'landing.builtfor.students.title': 'छात्र',
    'landing.builtfor.students.body': 'करियर का दबाव आने से पहले एक वेलबीइंग-केंद्रित ज़िंदगी की नींव रखें।',
    'landing.builtfor.professionals.title': 'पेशेवर',
    'landing.builtfor.professionals.body': 'प्रमोशन के बदले अपनी सेहत, रिश्ते और मक़सद कुर्बान करना बंद करें।',
    'landing.builtfor.entrepreneurs.title': 'उद्यमी',
    'landing.builtfor.entrepreneurs.body': 'आपका बिज़नेस आपकी वेलबीइंग से बेहतर नहीं चलेगा। दोनों को मापें।',
    'landing.builtfor.anyone.title': 'कोई भी',
    'landing.builtfor.anyone.body': 'जो भी एक मापी हुई, सोची-समझी, पूर्ण ज़िंदगी जीना चाहता है।',

    'landing.about.eyebrow': 'Clinity के बारे में',
    'landing.about.h2': 'वेलबीइंग एक एहसास नहीं — एक सिस्टम है।',
    'landing.about.body': 'Clinity एक डॉक्टर-नेतृत्व, AI-पावर्ड वेलबीइंग ऑपरेटिंग सिस्टम है। हम आपकी ज़िंदगी के 8 पहलू मापते हैं, छुपी हुई कमज़ोरियाँ निकालते हैं, और एक प्रिस्क्रिप्शन देते हैं जिसे आप वाकई फॉलो कर सकें।',

    'landing.founder.eyebrow': 'फाउंडर',
    'landing.founder.h2': 'मैंने Clinity क्यों बनाया',
    'landing.founder.body.p1': 'अपनी मेडिकल यात्रा के दौरान मुझे एहसास हुआ कि स्वास्थ्य, प्रोडक्टिविटी, फाइनेंस और पर्सनल ग्रोथ की अनगिनत जानकारी होने के बावजूद, जीवन बिखरा-बिखरा सा महसूस होता है।',
    'landing.founder.body.p2': 'कई लोगों की तरह, मैं भी जीवन के अलग-अलग पहलुओं को बेहतर बनाने की कोशिश कर रहा था, बिना उन्हें जोड़ने वाला कोई सिस्टम।',
    'landing.founder.body.p3': 'उसी अनुभव ने मुझे Clinity बनाने के लिए प्रेरित किया।',
    'landing.founder.name': 'डॉ. निषाद शशीन कुलकर्णी',
    'landing.founder.degree': 'MBBS',
    'landing.founder.role': 'फाउंडर, Clinity',
    'landing.founder.body': 'वर्षों तक ऐसे मरीज़ देखने के बाद जो काग़ज़ पर सफल थे लेकिन ज़िंदगी में थके हुए — मुझे एहसास हुआ कि वेलनेस को एक फ्रेमवर्क चाहिए, कोई और सप्लीमेंट, ऐप या टिप नहीं। Clinity वही फ्रेमवर्क है।',
    'landing.founder.creds': 'MBBS · फाउंडर, Clinity',

    'landing.problem.h2': 'असली समस्या जानकारी की कमी नहीं है।',
    'landing.problem.sub': 'ज़्यादातर लोग जानते हैं क्या करना चाहिए। चुनौती है — कहाँ से शुरू करें।',
    'landing.problem.card.clarity.title': 'स्पष्टता की कमी',
    'landing.problem.card.clarity.quote': "\u201Cमुझे नहीं पता असली रुकावट क्या है।\u201D",
    'landing.problem.card.overwhelm.title': 'भारीपन',
    'landing.problem.card.overwhelm.quote': "\u201Cसब कुछ ज़रूरी लगता है, शुरू कहाँ से करूँ?\u201D",
    'landing.problem.card.imbalance.title': 'असंतुलन',
    'landing.problem.card.imbalance.quote': "\u201Cएक चीज़ ठीक होती है, दूसरी बिगड़ जाती है।\u201D",
    'landing.problem.card.measurement.title': 'मापने का तरीक़ा नहीं',
    'landing.problem.card.measurement.quote': "\u201Cमेरे पास अपने वेलबीइंग को नापने का तरीक़ा ही नहीं है।\u201D",

    'landing.root.h2.line1': 'आपको मोटिवेशन की कमी नहीं है।',
    'landing.root.h2.line2': 'आपको एक सिस्टम की कमी है।',
    'landing.root.body': 'ज़्यादातर लोग सेहत, पैसा, करियर और रिश्ते अलग-अलग संभालते हैं। लेकिन वेलबीइंग एक सिस्टम है। बिना फ्रेमवर्क के, ज़िंदगी बस रिएक्टिव और बिखरी हुई चलती है।',

    'landing.solution.eyebrow': 'Clinity का तरीक़ा',
    'landing.solution.h2': 'मिलिए Clinity से',
    'landing.solution.sub': 'आपका पर्सनल वेलबीइंग ऑपरेटिंग सिस्टम।',
    'landing.solution.body': 'Clinity आपके वेलबीइंग के 8 पहलुओं को मापता है, छुपी कमज़ोरियाँ निकालता है, और एक पर्सनलाइज़्ड सिस्टम बनाता है।',

    'landing.how.h2': 'कैसे काम करता है',
    'landing.how.step1': '2-मिनट का असेसमेंट लें',
    'landing.how.step2': 'अपना वेलनेस स्कोर पाएं',
    'landing.how.step3': 'जानें क्या आपको रोक रहा है',
    'landing.how.step4': 'अपना पर्सनलाइज़्ड सिस्टम बनाएं',

    'landing.wheel.eyebrow': 'Clinity फ्रेमवर्क',
    'landing.wheel.h2': 'आपका वेलनेस स्कोर किन चीज़ों से बनता है',
    'landing.wheel.body': 'आपकी वेलबीइंग एक संख्या नहीं — यह 8 आपस में जुड़े पहलुओं का जोड़ है।',
    'landing.wheel.hint': 'किसी भी हिस्से पर hover करें — पूरी जानकारी देखें।',

    'landing.final.h2': 'ज़िंदगी सुधारने से पहले, उसे मापें।',
    'landing.final.sub': 'जानें आप कहाँ खड़े हैं और कौन-से पहलू आपको रोक रहे हैं।',
    'landing.final.cta': 'मेरा वेलनेस स्कोर पाएं',
    'landing.final.subtext': '2 मिनट। मुफ़्त असेसमेंट। तुरंत insights।',

    // Hindi — conversational, natural, friend-to-friend tone

    // Navbar
    'nav.problem': 'समस्या',
    'nav.solution': 'समाधान',
    'nav.outcomes': 'बेहतर नतीजे',
    'nav.founder': 'फाउंडर',
    'nav.cta': 'टेस्ट शुरू करें',

    // Hero
    'hero.title.line1': 'जानिए',
    'hero.title.line2': 'क्या आपको रोक रहा है',
    'hero.subtitle': 'Clinity का वेलनेस टेस्ट दीजिए और पता लगाइए कि ज़िंदगी के कौन-से हिस्से आपकी तरक्की को रोक रहे हैं।',
    'hero.cta.primary': 'टेस्ट शुरू करें',
    'hero.cta.secondary': 'और जानें',
    'hero.tag': 'डॉक्टर-नेतृत्व · AI-पावर्ड · 2 मिनट का टेस्ट',

    // Problem
    'problem.eyebrow': 'असली समस्या',
    'problem.title.part1': 'असली दिक्कत ',
    'problem.title.part2': 'जानकारी की नहीं है',
    'problem.caption.bold': 'सलाह की कमी नहीं है।',
    'problem.caption.rest': 'सिस्टम की कमी है।',

    // Solution
    'solution.eyebrow': 'समाधान',
    'solution.title.part1': 'बिखराव से ',
    'solution.title.part2': 'स्पष्टता तक',
    'solution.subtitle': 'Clinity ज़िंदगी की अहम चीज़ों को एक सिस्टम में जोड़ता है — खासतौर पर आपके लिए बना हुआ।',
    'solution.cta': 'टेस्ट शुरू करें',

    // Journey
    'journey.eyebrow': 'Clinity का सफर',
    'journey.title.part1': 'जुड़ी हुई ज़िंदगी के ',
    'journey.title.part2': 'तीन कदम',
    'journey.step1.title': 'टेस्ट',
    'journey.step1.desc': '2 मिनट का टेस्ट, 8 अहम पहलुओं पर।',
    'journey.step2.title': 'Clinity पर्सनलाइज़्ड प्लान',
    'journey.step2.desc': 'डॉक्टर और AI से बना, आपके लिए ख़ास रोडमैप।',
    'journey.step3.title': 'बेहतर नतीजे',
    'journey.step3.desc': 'ऐसा बदलाव जो दिखे, महसूस हो और ट्रैक हो सके।',

    // Outcomes
    'outcomes.eyebrow': 'बेहतर नतीजे',
    'outcomes.title.part1': 'आपको क्या ',
    'outcomes.title.part2': 'मिलेगा',
    'outcomes.clarity': 'साफ़ सोच',
    'outcomes.balance': 'संतुलन',
    'outcomes.performance': 'बेहतर परफॉर्मेंस',
    'outcomes.growth': 'तरक्की',
    'outcomes.resilience': 'मज़बूती',
    'outcomes.fulfillment': 'सुकून',
    'outcomes.longevity': 'लंबी ज़िंदगी',

    // Who
    'who.eyebrow': 'किसके लिए?',
    'who.title.part1': 'आपकी असली ज़िंदगी ',
    'who.title.part2': 'के लिए बना है',
    'who.students.name': 'स्टूडेंट्स',
    'who.students.tagline': 'पढ़ाई, फोकस और बैलेंस',
    'who.professionals.name': 'प्रोफेशनल्स',
    'who.professionals.tagline': 'बर्नआउट से निकलें, ज़िंदगी सम्भालें',
    'who.entrepreneurs.name': 'फाउंडर्स',
    'who.entrepreneurs.tagline': 'बेहतर वर्ज़न ख़ुद का बनाइए',
    'who.anyone.name': 'हर किसी के लिए',
    'who.anyone.tagline': 'बिखरी हुई सलाहों से थक चुके हैं',

    // Founder
    'founder.eyebrow': 'फाउंडर',
    'founder.title.part1': 'मैंने Clinity क्यों ',
    'founder.title.part2': 'बनाया',
    'founder.quote.p1': 'मेडिकल की पढ़ाई के दौरान मुझे एक बात समझ आई — सेहत, पैसा, करियर और खुद की तरक्की पर इतनी जानकारी होने के बावजूद, ज़िंदगी बिखरी हुई लगती है।',
    'founder.quote.p2': 'मेरी तरह बहुत-से लोग ज़िंदगी के अलग-अलग हिस्से सुधारने की कोशिश कर रहे हैं, पर उन्हें जोड़ने वाला कोई सिस्टम नहीं है।',
    'founder.quote.p3': 'इसी वजह से मैंने बनाया',
    'founder.role': 'फाउंडर, Clinity',

    // Follow
    'follow.eyebrow': 'जुड़े रहें',
    'follow.title.part1': 'देखिए हमारा ',
    'follow.title.part2': 'सफर',
    'follow.instagram': 'Instagram',
    'follow.instagram.handle': '@clinity',
    'follow.whatsapp': 'WhatsApp',
    'follow.whatsapp.handle': 'चैनल',
    'follow.youtube': 'YouTube',
    'follow.youtube.handle': 'सब्सक्राइब करें',
    'newsletter.title': 'हर हफ़्ते वेलनेस की समझ',
    'newsletter.subtitle': 'डॉक्टर के लिखे आसान आर्टिकल। स्पैम नहीं। कभी भी अनसब्सक्राइब करें।',
    'newsletter.placeholder': 'aap@example.com',
    'newsletter.cta': 'सब्सक्राइब',
    'newsletter.success': 'धन्यवाद — आप जुड़ गए हैं।',

    // Footer
    'footer.tagline': 'साथ चलते हैं वेलनेस की राह पर',
    'footer.line': 'अपनी वेलबीइंग का अपना सिस्टम बनाइए',
    'footer.doctorled': 'डॉक्टर-नेतृत्व',
    'footer.aipowered': 'AI-पावर्ड',
    'footer.copyright': '© 2026 Clinity. सर्वाधिकार सुरक्षित।',

    // Floating WhatsApp
    'wa.float.title': 'Clinity एक्सपर्ट से बात करें',
    'wa.float.subtitle': 'WhatsApp पर तुरंत सलाह — कोई इंतज़ार नहीं।',

    // Assessment
    'assess.brand': 'आपका वेलनेस टेस्ट',
    'assess.step': 'सवाल {current} / {total}',
    'assess.micro.start': 'अभी शुरुआत है…',
    'assess.micro.quarter': 'बढ़िया चल रहे हैं',
    'assess.micro.half': 'आधा रास्ता तय हो गया',
    'assess.micro.almost': 'बस थोड़ा बाक़ी',
    'assess.micro.last': 'आख़िरी सवाल',
    'assess.previous': '← पिछला सवाल',
    'assess.continue': 'आगे बढ़ें',
    'assess.section': 'सेक्शन {n} / {total}',
    'assess.loading': 'आपके जवाब समझ रहे हैं…',
    'assess.submit.error': 'सबमिट नहीं हो पाया। जवाब लोकल में सेव हैं।',
    'assess.submit.success': 'टेस्ट पूरा हुआ',

    // Dimension labels
    'dim.physical': 'शारीरिक',
    'dim.mental': 'मानसिक',
    'dim.emotional': 'भावनात्मक',
    'dim.financial': 'आर्थिक',
    'dim.career': 'करियर',
    'dim.lifestyle': 'लाइफ़स्टाइल और माहौल',
    'dim.spiritual': 'आध्यात्मिक',
    'dim.self_care': 'सेल्फ़-केयर',

    // Dimension intro explanations
    'dim.physical.text': 'शरीर ही आपकी ऊर्जा और लंबे समय की मेहनत की नींव है।',
    'dim.mental.text': 'दिमाग़ की शांति और तनाव पर कंट्रोल तय करता है कि आप कितना अच्छा सोच और काम कर पाएँगे।',
    'dim.emotional.text': 'भावनाओं की मज़बूती और रिश्ते आपकी खुशी और भीतर के संतुलन का आधार हैं।',
    'dim.financial.text': 'पैसा सीधे आपकी सुरक्षा, ज़िंदगी की पसंद और आज़ादी पर असर डालता है।',
    'dim.career.text': 'करियर की दिशा तय करती है कि आप ज़िंदगी में किधर जा रहे हैं।',
    'dim.lifestyle.text': 'आपकी रोज़ाना की आदतें और माहौल चुपके से आपकी ज़िंदगी की गुणवत्ता तय कर देते हैं।',
    'dim.spiritual.text': 'मक़सद और भीतर की शांति तय करते हैं कि आप कितने जुड़े और संतुष्ट महसूस करते हैं।',
    'dim.self_care.text': 'अपना ख़याल रखना और दिखना ख़ुद के लिए आपकी इज़्ज़त दिखाता है — और आत्मविश्वास बढ़ाता है।',

    // Result
    'result.title.part1': 'आपका ',
    'result.title.part2': 'वेलनेस रिज़ल्ट',
    'result.score.label': 'कुल स्कोर',
    'result.cta.whatsapp': 'WhatsApp पर मेरा वेलनेस प्लान भेजें',
    'result.cta.retake': 'दोबारा टेस्ट दें',
    'result.share.greeting': 'नमस्ते Clinity, मैंने अपना वेलनेस टेस्ट पूरा कर लिया है।',
    'result.share.name': 'नाम',
    'result.share.score': 'मेरा कुल स्कोर',
    'result.share.detailed': 'विस्तृत स्कोर:',
    'result.share.footer': 'कृपया मेरा पर्सनलाइज़्ड वेलनेस प्लान भेजें।',

    // Wellness Champion badge system (Hindi)
    'champion.congrats.eyebrow': '🎉 बधाई हो',
    'champion.celebration.title': 'आपने Clinity Wellness Champion बैज जीता',
    'champion.celebration.subtitle': 'जीवन के कई पहलुओं में मज़बूत वेलबीइंग दिखाने के लिए सम्मानित।',
    'champion.celebration.description': 'अपनी उपलब्धि शेयर करें, दूसरों को प्रेरित करें, और हमारे साथ वेलनेस की यात्रा जारी रखें।',
    'champion.name.label': 'बैज पर अपना नाम जोड़ें',
    'champion.name.placeholder': 'अपना नाम लिखें',
    'champion.name.hint': 'आपका बैज तुरंत पर्सनलाइज़ हो जाएगा — खाली रखने पर नाम नहीं दिखेगा।',
    'champion.share.instagram': 'Instagram',
    'champion.share.whatsapp': 'WhatsApp',
    'champion.share.linkedin': 'LinkedIn',
    'champion.share.download': 'डाउनलोड',
    'champion.share.downloaded': 'सेव हो गया',
    'champion.share.hint': 'शेयर करते समय #Clinity #WellnessChampion ज़रूर लगाएं',
    'champion.progress.eyebrow': 'आपकी वेलनेस स्टेज',
    'champion.stage.beginning.title': 'अपनी वेलनेस यात्रा की शुरुआत',
    'champion.stage.beginning.body': 'हर यात्रा जागरूकता से शुरू होती है। आपने सबसे ज़रूरी कदम उठाया — आज अपनी स्थिति को मापा।',
    'champion.stage.building.title': 'गति बना रहे हैं',
    'champion.stage.building.body': 'आप लंबे समय की वेलबीइंग की नींव बना रहे हैं। निरंतर बने रहें — छोटे कदम बड़े बदलाव लाते हैं।',
    'champion.stage.near.title': 'चैंपियन के क़रीब',
    'champion.stage.near.body': 'आप अच्छी प्रगति कर रहे हैं। Clinity Wellness Champion बनने में अब बस कुछ क़दम बाक़ी हैं। सुधार जारी रखें और फिर से असेसमेंट लें।',
    'champion.stage.champion.title': 'Clinity Wellness Champion',
    'champion.stage.champion.body': 'जीवन के कई पहलुओं में मज़बूत वेलबीइंग दिखाने के लिए सम्मानित। अपना शेयर करने योग्य Wellness Champion बैज पाएं।',
    'champion.locked.hint': '70+ स्कोर पाकर Clinity Wellness Champion बैज अनलॉक करें',

    'landing.champion.eyebrow': 'उपलब्धि',
    'landing.champion.h2': 'अच्छा स्कोर करें। अपना शेयर करने योग्य Wellness बैज पाएं।',
    'landing.champion.body': '70 या उससे ऊपर स्कोर करें और अपना Clinity Wellness Champion बैज अनलॉक करें। अपनी उपलब्धि शेयर करें और दूसरों को प्रेरित करें।',
    'landing.champion.cta': 'असेसमेंट दें',

    // Prescription paywall + flow (Hindi)
    'rx.locked.eyebrow': 'पर्सनलाइज़्ड वेलनेस प्रिस्क्रिप्शन',
    'rx.locked.title': 'अपना मुफ़्त डॉक्टर-नेतृत्व प्रिस्क्रिप्शन पाएं',
    'rx.locked.subtitle': 'आपके टेस्ट पर बना एक पर्सनलाइज़्ड वेलनेस प्लान — तुरंत WhatsApp पर। पूरी तरह मुफ़्त।',
    'rx.locked.bullet.diagnosis': 'आपके सबसे कम स्कोर वाले पहलुओं पर पर्सनलाइज़्ड वेलनेस इनसाइट्स',
    'rx.locked.bullet.rootcause': 'पहचानिए कौन-से प्रमुख क्षेत्र आपकी पूरी वेलबीइंग पर असर डाल रहे हैं',
    'rx.locked.bullet.systems': 'आपके असेसमेंट के आधार पर बना व्यवहारिक एक्शन प्लान',
    'rx.locked.bullet.medicines': 'सुझाई गई वेलनेस प्रैक्टिसेज़ और लाइफ़स्टाइल सुधार',
    'rx.locked.bullet.guidance': 'वेलबीइंग के हर पहलू में मार्गदर्शन',
    'rx.locked.bullet.outcome': '90 और 180 दिनों का वेलबीइंग सुधार रोडमैप',
    'rx.locked.cta': 'मेरा मुफ़्त प्रिस्क्रिप्शन पाएं',
    'rx.locked.tagline': 'Navigating Wellness Together™',
    'rx.locked.trust': 'डॉक्टर-समीक्षित · 100% मुफ़्त · WhatsApp पर तुरंत डिलीवरी',
    'rx.locked.free': 'मुफ़्त',
    'rx.locked.preview.diagnosis': 'वेलनेस इनसाइट्स',
    'rx.locked.preview.rootcause': 'विकास के क्षेत्र',
    'rx.locked.preview.systems': 'पर्सनलाइज़्ड एक्शन प्लान',
    'rx.locked.preview.medicines': 'सुझाई गई प्रैक्टिसेज़',

    // Premium / Priority Access section (Hindi)
    'rx.premium.eyebrow': 'विशेष एक्सेस',
    'rx.premium.title': 'प्रायोरिटी एक्सेस पाएं',
    'rx.premium.intro1': 'हर किसी को और जानकारी की ज़रूरत नहीं है।',
    'rx.premium.intro2': 'कुछ लोगों को चाहिए सही मार्गदर्शन, माहौल और सिस्टम।',
    'rx.premium.body': 'भविष्य के Clinity वेलनेस अनुभवों तक प्रायोरिटी एक्सेस पाएं — हर पहलू में बेहतर वेलबीइंग के लिए।',
    'rx.premium.b1.title': 'पर्सनलाइज़्ड वेलनेस कंसल्टेशन',
    'rx.premium.b1.body': 'आपके Wellness Score पर आधारित एक तैयार वेलबीइंग रिव्यू।',
    'rx.premium.b2.title': 'Wellness Spaces',
    'rx.premium.b2.body': 'स्वस्थ जीवन, रिकवरी, फ़ोकस, प्रोडक्टिविटी और व्यक्तिगत विकास के लिए डिज़ाइन किए गए वेलनेस-स्पेसेज़ की खोज करें।',
    'rx.premium.b3.title': 'Wellness Retreats',
    'rx.premium.b3.body': 'रिस्टोरेशन, स्पष्टता, संतुलन, परफ़ॉर्मेंस, वेलबीइंग और दीर्घायु पर केंद्रित विशेष रिट्रीट्स।',
    'rx.premium.b4.title': 'पर्सनलाइज़्ड वेलबीइंग रोडमैप',
    'rx.premium.b4.body': 'वेलबीइंग के हर पहलू पर एक्शनेबल गाइडेंस।',
    'rx.premium.b5.title': 'भविष्य के Clinity प्रोग्राम्स तक प्रायोरिटी एक्सेस',
    'rx.premium.b5.body': 'आने वाले Clinity अनुभवों, टूल्स, असेसमेंट्स और प्रीमियम वेलबीइंग सेवाओं का सबसे पहले एक्सेस।',
    'rx.premium.cta': 'प्रायोरिटी एक्सेस पाएं',
    'rx.premium.wa_message': 'Hi Clinity Team, I would like to get Priority Access to Clinity\'s premium wellness experiences, wellness spaces, retreats, and future offerings.',
    // Clinity Clinics waitlist hero
    'rx.clinics.eyebrow': 'जल्द आ रहा है',
    'rx.clinics.title': 'Clinity Clinics',
    'rx.clinics.subtitle': 'प्रीमियम इन-पर्सन वेलनेस क्लिनिक्स — आपके वेलबीइंग के हर पहलू को पूरा करने के लिए डिज़ाइन किए गए।',
    'rx.clinics.cta': 'वेटलिस्ट में शामिल हों',
    'rx.clinics.note': 'शुरुआती एक्सेस के इनवाइट्स WhatsApp पर भेजे जाएंगे',
    'rx.clinics.wa_message': 'Hi Clinity Team, I would like to join the Clinity Clinics waitlist for early access.',
    'rx.contact.title': 'WhatsApp पर हमें मैसेज करें',
    'rx.contact.subtitle': 'प्रायोरिटी एक्सेस पाने के लिए नीचे दिया गया मैसेज भेजें।',
    'rx.contact.number_label': 'WhatsApp नंबर',
    'rx.contact.message_label': 'तैयार मैसेज',
    'rx.contact.copy': 'कॉपी करें',
    'rx.contact.copied': 'कॉपी हो गया',
    'rx.contact.open_wa': 'WhatsApp खोलें',
    'rx.contact.help': 'अगर WhatsApp नहीं खुले, ऊपर का नंबर कॉपी कर अपने फ़ोन से हमें मैसेज करें।',
    'rx.contact.close': 'बंद करें',

    // Personalization form
    'rx.form.title': 'अपना प्रिस्क्रिप्शन पर्सनलाइज़ करें',
    'rx.form.subtitle': 'कुछ ज़रूरी जानकारी — ताकि Dr. Clinity आपके लिए ख़ास लिखें।',
    'rx.form.name': 'पूरा नाम',
    'rx.form.name.placeholder': 'आन्या शर्मा',
    'rx.form.age': 'उम्र',
    'rx.form.age.placeholder': '28',
    'rx.form.gender': 'लिंग',
    'rx.form.gender.female': 'महिला',
    'rx.form.gender.male': 'पुरुष',
    'rx.form.gender.other': 'अन्य',
    'rx.form.occupation': 'पेशा',
    'rx.form.occupation.placeholder': 'प्रोडक्ट डिज़ाइनर',
    'rx.form.city': 'शहर',
    'rx.form.city.placeholder': 'मुंबई',
    'rx.form.whatsapp': 'WhatsApp नंबर',
    'rx.form.whatsapp.placeholder': '+91 98765 43210',
    'rx.form.email': 'ईमेल (वैकल्पिक)',
    'rx.form.email.placeholder': 'you@example.com',
    'rx.form.email.invalid': 'कृपया सही ईमेल पता डालें',
    'rx.form.goal': 'मुख्य वेलनेस लक्ष्य',
    'rx.form.goal.energy': 'ऊर्जा और स्टैमिना बढ़ाना',
    'rx.form.goal.stress': 'तनाव और थकान कम करना',
    'rx.form.goal.career': 'करियर में स्पष्टता',
    'rx.form.goal.money': 'आर्थिक मज़बूती',
    'rx.form.goal.purpose': 'मक़सद और मतलब',
    'rx.form.goal.selfcare': 'सेल्फ़-केयर और आत्मविश्वास',
    'rx.form.goal.overall': 'समग्र वेलबीइंग',
    'rx.form.continue': 'मेरा प्रिस्क्रिप्शन पाएं',
    'rx.form.cancel': 'रद्द करें',
    'rx.form.privacy': 'यह जानकारी सिर्फ़ आपके प्रिस्क्रिप्शन के लिए है। कहीं शेयर नहीं की जाती।',
    'rx.form.required': 'कृपया सभी ज़रूरी फ़ील्ड भरें',

    // Generating loader
    'rx.gen.title': 'आपका वेलनेस प्रिस्क्रिप्शन तैयार हो रहा है…',
    'rx.gen.subtitle': 'Dr. Clinity आपके लिए ख़ास प्लान लिख रहे हैं। लगभग 60 सेकंड लगेंगे।',
    'rx.gen.step1': '8 पहलुओं का विश्लेषण',
    'rx.gen.step2': 'क्लिनिकल डायग्नोसिस लिखना',
    'rx.gen.step3': 'Clinity Systems™ असाइन करना',
    'rx.gen.step4': 'PDF + WhatsApp डिलीवरी तैयार करना',

    // Ready state
    'rx.ready.title': 'आपका प्रिस्क्रिप्शन तैयार है',
    'rx.ready.subtitle': 'आपका पर्सनलाइज़्ड Clinity Wellness Prescription WhatsApp पर भेज दिया गया है।',
    'rx.ready.saved': 'आपकी रिपोर्ट सुरक्षित रखी गई है — कभी भी एक्सेस करें।',
    'rx.ready.download': 'PDF डाउनलोड करें',
    'rx.ready.whatsapp': 'मेरा प्रिस्क्रिप्शन WhatsApp पर देखें',
    'rx.ready.view': 'मेरा प्रिस्क्रिप्शन देखें',
    'rx.ready.retake': 'फिर से असेसमेंट लें',
    'rx.ready.failsafe.title': 'WhatsApp डिलीवरी हो रही है',
    'rx.ready.failsafe.body': 'चिंता न करें — आपका प्रिस्क्रिप्शन यहाँ सुरक्षित है:',

    // Errors
    'rx.error.payment': 'पेमेंट नहीं हुआ। कृपया दोबारा कोशिश करें।',
    'rx.error.generation': 'प्रिस्क्रिप्शन नहीं बना पाए। हमारी टीम को सूचना भेज दी गई है।',
    'rx.error.network': 'नेटवर्क समस्या। दोबारा कोशिश करें।',
  },

  mr: {
    // ===================== LANDING (new design) =====================
    'landing.nav.cta': 'माझा स्कोर मिळवा',
    'landing.hero.trust': 'डॉक्टर-निर्मित • AI-पॉवर्ड • 2-मिनिटांचं असेसमेंट',
    'landing.hero.h1.line1': 'तुम्ही एका क्षेत्रात जिंकू शकता',
    'landing.hero.h1.line2': 'तरीही अडकल्यासारखं वाटू शकतं.',
    'landing.hero.synthesis': 'जेव्हा सर्व काही एकत्र काम करतं, तेव्हा जीवन उत्तम चालतं.',
    'landing.hero.sub': 'तुम्हाला तुमचं वय माहीत आहे. तुमचा वेलनेस स्कोर माहीत आहे का?',
    'landing.hero.supporting': 'जे पैलू खरोखर महत्त्वाचे आहेत — त्यांवर तुम्ही कुठे आहात ते मोजा.',
    'landing.hero.primary': 'माझा वेलनेस स्कोर मिळवा',
    'landing.hero.secondary': 'अधिक जाणून घ्या',

    'landing.outcomes.h2': 'सर्व पैलूंमध्ये उत्तम परिणाम',
    'landing.outcomes.sub': 'मोजा. समजून घ्या. सुधारा.',

    'landing.wheel.eyebrow': 'Clinity फ्रेमवर्क',
    'landing.wheel.h2': 'तुमचा वेलनेस स्कोर कशापासून बनतो',
    'landing.wheel.sub': 'तुमची वेलबीइंग एक आकडा नाही — ती 8 परस्परजोडलेल्या पैलूंचं संयोजन आहे.',
    'landing.wheel.hint': 'कोणत्याही भागावर hover करा.',

    'landing.unlock.eyebrow': 'काय मिळेल',
    'landing.unlock.h2': 'काय मिळेल',
    'landing.unlock.sub': 'एक वेलबीइंग सिस्टम जी काळानुसार वाढत जाते.',
    'landing.unlock.clarity': 'स्पष्टता',
    'landing.unlock.balance': 'संतुलन',
    'landing.unlock.performance': 'कामगिरी',
    'landing.unlock.growth': 'वाढ',
    'landing.unlock.resilience': 'सहनशक्ती',
    'landing.unlock.fulfillment': 'समाधान',
    'landing.unlock.longevity': 'दीर्घायुष्य',

    'landing.builtfor.h2': 'कोणासाठी',
    'landing.builtfor.students.title': 'विद्यार्थी',
    'landing.builtfor.students.body': 'करियरचा ताण येण्याआधी वेलबीइंग-केंद्रित आयुष्याचा पाया घाला.',
    'landing.builtfor.professionals.title': 'व्यावसायिक',
    'landing.builtfor.professionals.body': 'प्रमोशनसाठी आरोग्य, नातेसंबंध आणि उद्देश त्याग करणं थांबवा.',
    'landing.builtfor.entrepreneurs.title': 'उद्योजक',
    'landing.builtfor.entrepreneurs.body': 'तुमचा व्यवसाय तुमच्या वेलबीइंगपेक्षा चांगला चालणार नाही. दोन्ही मोजा.',
    'landing.builtfor.anyone.title': 'कोणीही',
    'landing.builtfor.anyone.body': 'जो कोणी मोजलेलं, हेतुपूर्ण, परिपूर्ण आयुष्य जगू इच्छितो.',

    'landing.about.eyebrow': 'Clinity बद्दल',
    'landing.about.h2': 'वेलबीइंग ही भावना नाही — एक सिस्टम आहे.',
    'landing.about.body': 'Clinity ही डॉक्टर-निर्मित, AI-पॉवर्ड वेलबीइंग ऑपरेटिंग सिस्टम आहे. आम्ही तुमच्या आयुष्याचे 8 पैलू मोजतो, लपलेल्या कमतरता शोधतो, आणि असं प्रिस्क्रिप्शन देतो जे तुम्ही खरंच पाळू शकता.',

    'landing.founder.eyebrow': 'फाउंडर',
    'landing.founder.h2': 'मी Clinity का बनवलं',
    'landing.founder.body.p1': 'माझ्या वैद्यकीय प्रवासात मला जाणवलं की आरोग्य, प्रोडक्टिव्हिटी, फायनान्स आणि पर्सनल ग्रोथबद्दल अमर्याद माहिती असूनही, आयुष्य अजूनही विखुरलेलं वाटतं.',
    'landing.founder.body.p2': 'इतर अनेक लोकांप्रमाणे, मीही आयुष्याच्या वेगवेगळ्या पैलूंमध्ये सुधारणा करण्याचा प्रयत्न करत होतो, पण त्यांना जोडणारी कोणतीही सिस्टम नव्हती.',
    'landing.founder.body.p3': 'त्याच अनुभवामुळे मी Clinity बनवलं.',
    'landing.founder.name': 'डॉ. निषाद शशीन कुलकर्णी',
    'landing.founder.degree': 'MBBS',
    'landing.founder.role': 'फाउंडर, Clinity',
    'landing.founder.body': 'कागदावर यशस्वी पण आयुष्यात थकलेल्या रुग्णांवर वर्षानुवर्षं उपचार केल्यानंतर — मला जाणवलं की वेलनेसला एक फ्रेमवर्क हवं, आणखी एक सप्लिमेंट, अ‍ॅप किंवा सल्ला नाही. Clinity तेच फ्रेमवर्क आहे.',
    'landing.founder.creds': 'MBBS · फाउंडर, Clinity',

    'landing.problem.h2': 'खरी समस्या माहितीची कमतरता नाही.',
    'landing.problem.sub': 'बहुतेकांना माहीत असतं काय करायचं. अडचण आहे — कुठून सुरुवात करायची.',
    'landing.problem.card.clarity.title': 'स्पष्टतेचा अभाव',
    'landing.problem.card.clarity.quote': "\u201Cमला माहीत नाही नक्की काय अडवतंय.\u201D",
    'landing.problem.card.overwhelm.title': 'गोंधळ',
    'landing.problem.card.overwhelm.quote': "\u201Cसगळं महत्त्वाचं वाटतं, सुरुवात कुठून?\u201D",
    'landing.problem.card.imbalance.title': 'असंतुलन',
    'landing.problem.card.imbalance.quote': "\u201Cएक गोष्ट सुधारते, दुसरी बिघडते.\u201D",
    'landing.problem.card.measurement.title': 'मोजमाप नाही',
    'landing.problem.card.measurement.quote': "\u201Cमाझ्याकडे माझं वेलबीइंग मोजायचा कोणताच मार्ग नाही.\u201D",

    'landing.root.h2.line1': 'तुमच्याकडे मोटिवेशनची कमी नाही.',
    'landing.root.h2.line2': 'तुमच्याकडे सिस्टमची कमी आहे.',
    'landing.root.body': 'बहुतेक लोक आरोग्य, पैसा, करियर आणि नातेसंबंध वेगवेगळे सांभाळतात. पण वेलबीइंग ही एक सिस्टम आहे. फ्रेमवर्कशिवाय आयुष्य प्रतिक्रियावादी आणि विखुरलेलं होतं.',

    'landing.solution.eyebrow': 'Clinity चा मार्ग',
    'landing.solution.h2': 'भेटा Clinity ला',
    'landing.solution.sub': 'तुमची वैयक्तिक वेलबीइंग ऑपरेटिंग सिस्टम.',
    'landing.solution.body': 'Clinity तुमच्या वेलबीइंगचे 8 पैलू मोजते, लपलेल्या कमतरता शोधते, आणि दीर्घकालीन वाढीसाठी सिस्टम बनवते.',

    'landing.how.h2': 'कसं काम करतं',
    'landing.how.step1': '2-मिनिटांचं असेसमेंट घ्या',
    'landing.how.step2': 'तुमचा वेलनेस स्कोर मिळवा',
    'landing.how.step3': 'काय तुम्हाला अडवतंय ते जाणून घ्या',
    'landing.how.step4': 'तुमची पर्सनलाइज़्ड सिस्टम बनवा',

    'landing.wheel.eyebrow': 'Clinity फ्रेमवर्क',
    'landing.wheel.h2': 'तुमचा वेलनेस स्कोर कशापासून बनतो',
    'landing.wheel.body': 'तुमची वेलबीइंग एक आकडा नाही — ती 8 परस्परजोडलेल्या पैलूंचं संयोजन आहे.',
    'landing.wheel.hint': 'कोणत्याही भागावर hover करा — पूर्ण माहिती पाहा.',

    'landing.final.h2': 'आयुष्य सुधारण्याआधी, ते मोजा.',
    'landing.final.sub': 'तुम्ही कुठे आहात आणि कोणते पैलू तुम्हाला अडवत आहेत हे जाणून घ्या.',
    'landing.final.cta': 'माझा वेलनेस स्कोर मिळवा',
    'landing.final.subtext': '2 मिनिटं. मोफत असेसमेंट. तातडीनं insights.',

    // Marathi — conversational, friend-to-friend tone

    // Navbar
    'nav.problem': 'समस्या',
    'nav.solution': 'उपाय',
    'nav.outcomes': 'उत्तम परिणाम',
    'nav.founder': 'फाउंडर',
    'nav.cta': 'टेस्ट सुरू करा',

    // Hero
    'hero.title.line1': 'ओळखा',
    'hero.title.line2': 'तुम्हाला कशामुळे थांबवलंय',
    'hero.subtitle': 'Clinity चा वेलनेस टेस्ट द्या आणि शोधा — आयुष्याच्या कोणत्या गोष्टी तुमची प्रगती थांबवत आहेत.',
    'hero.cta.primary': 'टेस्ट सुरू करा',
    'hero.cta.secondary': 'अधिक जाणून घ्या',
    'hero.tag': 'डॉक्टर-निर्मित · AI-पॉवर्ड · 2 मिनिटांचा टेस्ट',

    // Problem
    'problem.eyebrow': 'खरी समस्या',
    'problem.title.part1': 'खरी अडचण ',
    'problem.title.part2': 'माहितीची नाही',
    'problem.caption.bold': 'सल्ले अमाप आहेत.',
    'problem.caption.rest': 'पण सिस्टम कुठेच नाही.',

    // Solution
    'solution.eyebrow': 'उपाय',
    'solution.title.part1': 'विस्कळीतपणाकडून ',
    'solution.title.part2': 'स्पष्टतेकडे',
    'solution.subtitle': 'Clinity आयुष्यातल्या महत्त्वाच्या गोष्टी एका सिस्टममध्ये जोडतो — फक्त तुमच्यासाठी बनवलेला.',
    'solution.cta': 'टेस्ट सुरू करा',

    // Journey
    'journey.eyebrow': 'Clinity चा प्रवास',
    'journey.title.part1': 'जोडलेल्या आयुष्याकडे ',
    'journey.title.part2': 'तीन पावलं',
    'journey.step1.title': 'टेस्ट',
    'journey.step1.desc': '2 मिनिटांचा टेस्ट, आयुष्याच्या 8 अंगांवर.',
    'journey.step2.title': 'Clinity पर्सनलाइज़्ड प्लॅन',
    'journey.step2.desc': 'डॉक्टर आणि AI नी मिळून बनवलेला, तुमच्यासाठीचा रोडमॅप.',
    'journey.step3.title': 'उत्तम परिणाम',
    'journey.step3.desc': 'दिसणारा, जाणवणारा आणि ट्रॅक करता येणारा बदल.',

    // Outcomes
    'outcomes.eyebrow': 'उत्तम परिणाम',
    'outcomes.title.part1': 'तुम्हाला काय ',
    'outcomes.title.part2': 'मिळेल',
    'outcomes.clarity': 'स्पष्टता',
    'outcomes.balance': 'संतुलन',
    'outcomes.performance': 'चांगली कामगिरी',
    'outcomes.growth': 'प्रगती',
    'outcomes.resilience': 'खंबीरपणा',
    'outcomes.fulfillment': 'समाधान',
    'outcomes.longevity': 'दीर्घायुष्य',

    // Who
    'who.eyebrow': 'कोणासाठी?',
    'who.title.part1': 'तुमच्या खऱ्या ',
    'who.title.part2': 'आयुष्यासाठी',
    'who.students.name': 'विद्यार्थी',
    'who.students.tagline': 'अभ्यास, फोकस आणि बॅलन्स',
    'who.professionals.name': 'प्रोफेशनल्स',
    'who.professionals.tagline': 'बर्नआउट थांबवा, आयुष्य सावरा',
    'who.entrepreneurs.name': 'फाउंडर्स',
    'who.entrepreneurs.tagline': 'स्वतःचा उत्तम वर्जन घडवा',
    'who.anyone.name': 'सगळ्यांसाठी',
    'who.anyone.tagline': 'विस्कळीत सल्ल्यांना कंटाळलेल्यांसाठी',

    // Founder
    'founder.eyebrow': 'फाउंडर',
    'founder.title.part1': 'मी Clinity का ',
    'founder.title.part2': 'बनवलं',
    'founder.quote.p1': 'मेडिकलच्या शिक्षणादरम्यान मला जाणवलं — आरोग्य, पैसा, करियर आणि स्वतःच्या प्रगतीची भरपूर माहिती असूनही, आयुष्य विस्कळीत वाटतं.',
    'founder.quote.p2': 'माझ्यासारखेच अनेक जण आयुष्याचे वेगवेगळे भाग सुधारायचा प्रयत्न करत असतात, पण त्यांना जोडणारी कोणतीच सिस्टम नसते.',
    'founder.quote.p3': 'याच अनुभवातून मी बनवलं',
    'founder.role': 'फाउंडर, Clinity',

    // Follow
    'follow.eyebrow': 'जोडलेले राहा',
    'follow.title.part1': 'पाहा आमचा ',
    'follow.title.part2': 'प्रवास',
    'follow.instagram': 'Instagram',
    'follow.instagram.handle': '@clinity',
    'follow.whatsapp': 'WhatsApp',
    'follow.whatsapp.handle': 'चॅनेल',
    'follow.youtube': 'YouTube',
    'follow.youtube.handle': 'सबस्क्राइब करा',
    'newsletter.title': 'दर आठवड्याला वेलनेस टिप्स',
    'newsletter.subtitle': 'डॉक्टरांनी लिहिलेले सोपे लेख. स्पॅम नाही. कधीही अनसबस्क्राइब करा.',
    'newsletter.placeholder': 'tumhi@example.com',
    'newsletter.cta': 'सबस्क्राइब',
    'newsletter.success': 'धन्यवाद — तुम्ही यादीत आहात.',

    // Footer
    'footer.tagline': 'वेलनेसच्या वाटेवर एकत्र',
    'footer.line': 'स्वतःची वेलबीइंग सिस्टम बनवा',
    'footer.doctorled': 'डॉक्टर-निर्मित',
    'footer.aipowered': 'AI-पॉवर्ड',
    'footer.copyright': '© 2026 Clinity. सर्व हक्क राखीव.',

    // Floating WhatsApp
    'wa.float.title': 'Clinity एक्सपर्टशी बोला',
    'wa.float.subtitle': 'WhatsApp वर लगेच मार्गदर्शन — काही वेळ नाही.',

    // Assessment
    'assess.brand': 'तुमचा वेलनेस टेस्ट',
    'assess.step': 'प्रश्न {current} / {total}',
    'assess.micro.start': 'नुकतीच सुरुवात…',
    'assess.micro.quarter': 'छान चाललंय',
    'assess.micro.half': 'अर्धा प्रवास झालाय',
    'assess.micro.almost': 'जवळजवळ संपलं',
    'assess.micro.last': 'शेवटचा प्रश्न',
    'assess.previous': '← मागचा प्रश्न',
    'assess.continue': 'पुढे जा',
    'assess.section': 'विभाग {n} / {total}',
    'assess.loading': 'तुमची उत्तरं समजून घेतोय…',
    'assess.submit.error': 'सबमिट झालं नाही. उत्तरं तुमच्या डिव्हाइसवर सेव्ह आहेत.',
    'assess.submit.success': 'टेस्ट पूर्ण झाला',

    // Dimensions
    'dim.physical': 'शारीरिक',
    'dim.mental': 'मानसिक',
    'dim.emotional': 'भावनिक',
    'dim.financial': 'आर्थिक',
    'dim.career': 'करियर',
    'dim.lifestyle': 'लाइफस्टाइल आणि वातावरण',
    'dim.spiritual': 'आध्यात्मिक',
    'dim.self_care': 'सेल्फ-केअर',

    // Dimension intro
    'dim.physical.text': 'शरीर हाच तुमच्या ऊर्जेचा आणि दीर्घकाळ टिकणाऱ्या कामगिरीचा पाया आहे.',
    'dim.mental.text': 'मनाची स्वच्छता आणि ताणावरचा ताबा ठरवतो तुम्ही किती चांगलं विचार करता आणि काम करता.',
    'dim.emotional.text': 'भावनिक स्थिरता आणि नाती ही तुमच्या आनंदाचा आणि आंतरिक शांतीचा आधार आहेत.',
    'dim.financial.text': 'पैसा थेट तुमच्या सुरक्षिततेवर आणि भविष्यातील स्वातंत्र्यावर परिणाम करतो.',
    'dim.career.text': 'करियरची दिशा ठरवते तुम्ही आयुष्यात कुठे चालला आहात.',
    'dim.lifestyle.text': 'तुमच्या रोजच्या सवयी आणि आजूबाजूचं वातावरण हळुहळू तुमची संपूर्ण आयुष्याची गुणवत्ता ठरवतं.',
    'dim.spiritual.text': 'जीवनाचा हेतू आणि आंतरिक शांती ठरवते तुम्ही किती समाधानी वाटता.',
    'dim.self_care.text': 'स्वतःची काळजी आणि वावरणं — हे दाखवतं तुम्ही स्वतःला किती किंमत देता.',

    // Result
    'result.title.part1': 'तुमचा ',
    'result.title.part2': 'वेलनेस रिझल्ट',
    'result.score.label': 'एकूण स्कोर',
    'result.cta.whatsapp': 'WhatsApp वर माझा वेलनेस प्लॅन पाठवा',
    'result.cta.retake': 'पुन्हा टेस्ट द्या',
    'result.share.greeting': 'नमस्कार Clinity, मी माझा वेलनेस टेस्ट पूर्ण केला आहे.',
    'result.share.name': 'नाव',
    'result.share.score': 'माझा एकूण स्कोर',
    'result.share.detailed': 'तपशीलवार स्कोर:',
    'result.share.footer': 'कृपया माझा पर्सनलाइज़्ड वेलनेस प्लॅन पाठवा.',

    // Wellness Champion badge system (Marathi)
    'champion.congrats.eyebrow': '🎉 अभिनंदन',
    'champion.celebration.title': 'तुम्ही Clinity Wellness Champion बॅज मिळवला',
    'champion.celebration.subtitle': 'आयुष्याच्या अनेक पैलूंमध्ये मजबूत वेलबीइंग दाखवल्याबद्दल सन्मानित.',
    'champion.celebration.description': 'तुमची उपलब्धी शेअर करा, इतरांना प्रेरित करा आणि आमच्यासोबत वेलनेसचा प्रवास सुरू ठेवा.',
    'champion.name.label': 'बॅजवर तुमचं नाव जोडा',
    'champion.name.placeholder': 'तुमचं नाव लिहा',
    'champion.name.hint': 'तुमचा बॅज लगेच पर्सनलाइज़ होईल — रिकामं ठेवल्यास नाव दिसणार नाही.',
    'champion.share.instagram': 'Instagram',
    'champion.share.whatsapp': 'WhatsApp',
    'champion.share.linkedin': 'LinkedIn',
    'champion.share.download': 'डाउनलोड',
    'champion.share.downloaded': 'सेव झालं',
    'champion.share.hint': 'शेअर करताना #Clinity #WellnessChampion वापरा',
    'champion.progress.eyebrow': 'तुमची वेलनेस स्टेज',
    'champion.stage.beginning.title': 'तुमच्या वेलनेस प्रवासाची सुरुवात',
    'champion.stage.beginning.body': 'प्रत्येक प्रवास जागरूकतेने सुरू होतो. तुम्ही सर्वात महत्त्वाचं पाऊल उचललंय — आज तुमची स्थिती मोजली.',
    'champion.stage.building.title': 'गती निर्माण करत आहात',
    'champion.stage.building.body': 'तुम्ही दीर्घकालीन वेलबीइंगचा पाया रचत आहात. सातत्य ठेवा — छोटे पाऊलं मोठा बदल घडवतात.',
    'champion.stage.near.title': 'चॅम्पियनच्या जवळ',
    'champion.stage.near.body': 'तुम्ही चांगली प्रगती करत आहात. Clinity Wellness Champion बनण्यासाठी फक्त काही पाऊलं उरली आहेत. सुधारणा सुरू ठेवा आणि पुन्हा असेसमेंट घ्या.',
    'champion.stage.champion.title': 'Clinity Wellness Champion',
    'champion.stage.champion.body': 'आयुष्याच्या अनेक पैलूंमध्ये मजबूत वेलबीइंग दाखवल्याबद्दल सन्मानित. तुमचा शेअर करण्यायोग्य Wellness Champion बॅज मिळवा.',
    'champion.locked.hint': '70+ स्कोर मिळवून Clinity Wellness Champion बॅज अनलॉक करा',

    'landing.champion.eyebrow': 'उपलब्धी',
    'landing.champion.h2': 'चांगला स्कोर करा. तुमचा शेअर करण्यायोग्य Wellness बॅज मिळवा.',
    'landing.champion.body': '70 किंवा त्याहून जास्त स्कोर करा आणि तुमचा Clinity Wellness Champion बॅज अनलॉक करा. तुमची उपलब्धी शेअर करा आणि इतरांना प्रेरित करा.',
    'landing.champion.cta': 'असेसमेंट घ्या',

    // Prescription paywall + flow (Marathi)
    'rx.locked.eyebrow': 'पर्सनलाइज़्ड वेलनेस प्रिस्क्रिप्शन',
    'rx.locked.title': 'तुमचं मोफत डॉक्टर-निर्मित प्रिस्क्रिप्शन मिळवा',
    'rx.locked.subtitle': 'तुमच्या टेस्टवर बनवलेला पर्सनलाइज़्ड वेलनेस प्लॅन — तातडीनं WhatsApp वर. पूर्णपणे मोफत.',
    'rx.locked.bullet.diagnosis': 'तुमच्या कमी स्कोर असलेल्या पैलूंवर पर्सनलाइज़्ड वेलनेस इनसाइट्स',
    'rx.locked.bullet.rootcause': 'तुमच्या एकूण वेलबीइंगवर परिणाम करणारे मुख्य भाग ओळखा',
    'rx.locked.bullet.systems': 'तुमच्या असेसमेंटवर आधारित व्यवहारिक अ‍ॅक्शन प्लॅन',
    'rx.locked.bullet.medicines': 'सुचवलेल्या वेलनेस प्रॅक्टिसेस आणि जीवनशैली सुधारणा',
    'rx.locked.bullet.guidance': 'वेलबीइंगच्या प्रत्येक पैलूवर मार्गदर्शन',
    'rx.locked.bullet.outcome': '90 आणि 180 दिवसांचा वेलबीइंग सुधारणा रोडमॅप',
    'rx.locked.cta': 'माझं मोफत प्रिस्क्रिप्शन मिळवा',
    'rx.locked.tagline': 'Navigating Wellness Together™',
    'rx.locked.trust': 'डॉक्टर-समीक्षित · १००% मोफत · WhatsApp वर तातडीनं',
    'rx.locked.free': 'मोफत',
    'rx.locked.preview.diagnosis': 'वेलनेस इनसाइट्स',
    'rx.locked.preview.rootcause': 'विकासाचे क्षेत्र',
    'rx.locked.preview.systems': 'पर्सनलाइज़्ड अ‍ॅक्शन प्लॅन',
    'rx.locked.preview.medicines': 'सुचवलेल्या प्रॅक्टिसेस',

    // Premium / Priority Access section (Marathi)
    'rx.premium.eyebrow': 'विशेष प्रवेश',
    'rx.premium.title': 'प्रायोरिटी अ‍ॅक्सेस मिळवा',
    'rx.premium.intro1': 'प्रत्येकाला आणखी माहितीची गरज नसते.',
    'rx.premium.intro2': 'काहींना हवं असतं — योग्य मार्गदर्शन, वातावरण आणि सिस्टीम.',
    'rx.premium.body': 'भविष्यातील Clinity वेलनेस अनुभवांना प्रायोरिटी अ‍ॅक्सेस मिळवा — वेलबीइंगच्या प्रत्येक पैलूत सुधारणेसाठी.',
    'rx.premium.b1.title': 'पर्सनलाइज़्ड वेलनेस कन्सल्टेशन',
    'rx.premium.b1.body': 'तुमच्या Wellness Score वर आधारित वेलबीइंग रिव्ह्यू.',
    'rx.premium.b2.title': 'Wellness Spaces',
    'rx.premium.b2.body': 'निरोगी जीवन, रिकव्हरी, फोकस, प्रोडक्टिव्हिटी आणि वैयक्तिक विकासासाठी डिझाइन केलेल्या वेलनेस स्पेसेस.',
    'rx.premium.b3.title': 'Wellness Retreats',
    'rx.premium.b3.body': 'रिस्टोरेशन, स्पष्टता, संतुलन, परफॉर्मन्स, वेलबीइंग आणि दीर्घायुष्यावर केंद्रित विशेष रिट्रीट्स.',
    'rx.premium.b4.title': 'पर्सनलाइज़्ड वेलबीइंग रोडमॅप',
    'rx.premium.b4.body': 'वेलबीइंगच्या प्रत्येक पैलूवर अ‍ॅक्शन घेण्यासारखं मार्गदर्शन.',
    'rx.premium.b5.title': 'भविष्यातील Clinity प्रोग्राम्सना प्रायोरिटी अ‍ॅक्सेस',
    'rx.premium.b5.body': 'येणाऱ्या Clinity अनुभवांना, टूल्सना, असेसमेंट्सना आणि प्रीमियम वेलबीइंग सेवांना सर्वात आधी अ‍ॅक्सेस.',
    'rx.premium.cta': 'प्रायोरिटी अ‍ॅक्सेस मिळवा',
    'rx.premium.wa_message': 'Hi Clinity Team, I would like to get Priority Access to Clinity\'s premium wellness experiences, wellness spaces, retreats, and future offerings.',
    // Clinity Clinics waitlist hero
    'rx.clinics.eyebrow': 'लवकरच येत आहे',
    'rx.clinics.title': 'Clinity Clinics',
    'rx.clinics.subtitle': 'प्रीमियम इन-पर्सन वेलनेस क्लिनिक्स — तुमच्या वेलबीइंगचा प्रत्येक पैलू पूर्ण करण्यासाठी डिझाइन केलेली.',
    'rx.clinics.cta': 'वेटलिस्टमध्ये सामील व्हा',
    'rx.clinics.note': 'सुरुवातीच्या अ‍ॅक्सेससाठी WhatsApp वर इनव्हाइट्स पाठवली जातील',
    'rx.clinics.wa_message': 'Hi Clinity Team, I would like to join the Clinity Clinics waitlist for early access.',
    'rx.contact.title': 'WhatsApp वर आम्हाला संदेश पाठवा',
    'rx.contact.subtitle': 'प्रायोरिटी अ‍ॅक्सेस मिळवण्यासाठी हा संदेश पाठवा.',
    'rx.contact.number_label': 'WhatsApp क्रमांक',
    'rx.contact.message_label': 'तयार संदेश',
    'rx.contact.copy': 'कॉपी करा',
    'rx.contact.copied': 'कॉपी झालं',
    'rx.contact.open_wa': 'WhatsApp उघडा',
    'rx.contact.help': 'जर WhatsApp उघडलं नाही, वरचा क्रमांक कॉपी करून तुमच्या फोनवरून संदेश पाठवा.',
    'rx.contact.close': 'बंद करा',

    // Personalization form
    'rx.form.title': 'तुमचं प्रिस्क्रिप्शन पर्सनलाइज़ करा',
    'rx.form.subtitle': 'थोडी माहिती — जेणेकरून Dr. Clinity फक्त तुमच्यासाठी लिहितील.',
    'rx.form.name': 'पूर्ण नाव',
    'rx.form.name.placeholder': 'आन्या शर्मा',
    'rx.form.age': 'वय',
    'rx.form.age.placeholder': '28',
    'rx.form.gender': 'लिंग',
    'rx.form.gender.female': 'महिला',
    'rx.form.gender.male': 'पुरुष',
    'rx.form.gender.other': 'इतर',
    'rx.form.occupation': 'व्यवसाय',
    'rx.form.occupation.placeholder': 'प्रोडक्ट डिझायनर',
    'rx.form.city': 'शहर',
    'rx.form.city.placeholder': 'मुंबई',
    'rx.form.whatsapp': 'WhatsApp नंबर',
    'rx.form.whatsapp.placeholder': '+91 98765 43210',
    'rx.form.email': 'ईमेल (पर्यायी)',
    'rx.form.email.placeholder': 'you@example.com',
    'rx.form.email.invalid': 'कृपया योग्य ईमेल टाका',
    'rx.form.goal': 'मुख्य वेलनेस ध्येय',
    'rx.form.goal.energy': 'ऊर्जा आणि स्टॅमिना वाढवा',
    'rx.form.goal.stress': 'ताण कमी करा',
    'rx.form.goal.career': 'करियरमध्ये स्पष्टता',
    'rx.form.goal.money': 'आर्थिक स्थैर्य',
    'rx.form.goal.purpose': 'जीवनाचा हेतू',
    'rx.form.goal.selfcare': 'सेल्फ-केअर आणि आत्मविश्वास',
    'rx.form.goal.overall': 'संपूर्ण वेलबीइंग',
    'rx.form.continue': 'माझं प्रिस्क्रिप्शन मिळवा',
    'rx.form.cancel': 'रद्द करा',
    'rx.form.privacy': 'ही माहिती फक्त तुमच्या प्रिस्क्रिप्शनसाठी आहे. कुठेच शेअर केली जात नाही.',
    'rx.form.required': 'कृपया सर्व आवश्यक फील्ड भरा',

    // Generating loader
    'rx.gen.title': 'तुमचं वेलनेस प्रिस्क्रिप्शन तयार होतंय…',
    'rx.gen.subtitle': 'Dr. Clinity तुमच्यासाठी खास प्लॅन लिहित आहेत. साधारण 60 सेकंद लागतील.',
    'rx.gen.step1': '8 अंगांचं विश्लेषण',
    'rx.gen.step2': 'क्लिनिकल डायग्नोसिस लिहिणं',
    'rx.gen.step3': 'Clinity Systems™ नेमणं',
    'rx.gen.step4': 'PDF + WhatsApp डिलिव्हरी',

    // Ready state
    'rx.ready.title': 'तुमचं प्रिस्क्रिप्शन तयार आहे',
    'rx.ready.subtitle': 'तुमचं पर्सनलाइज़्ड Clinity Wellness Prescription WhatsApp वर पाठवलं आहे.',
    'rx.ready.saved': 'तुमचा रिपोर्ट सुरक्षितपणे साठवला आहे — कधीही पाहता येईल.',
    'rx.ready.download': 'PDF डाउनलोड करा',
    'rx.ready.whatsapp': 'माझं प्रिस्क्रिप्शन WhatsApp वर पाहा',
    'rx.ready.view': 'माझं प्रिस्क्रिप्शन पाहा',
    'rx.ready.retake': 'पुन्हा असेसमेंट घ्या',
    'rx.ready.failsafe.title': 'WhatsApp डिलिव्हरी होत आहे',
    'rx.ready.failsafe.body': 'काळजी नका — तुमचं प्रिस्क्रिप्शन इथे सुरक्षित आहे:',

    // Errors
    'rx.error.payment': 'पेमेंट झालं नाही. पुन्हा प्रयत्न करा.',
    'rx.error.generation': 'प्रिस्क्रिप्शन तयार होऊ शकलं नाही. आमची टीम सूचित आहे.',
    'rx.error.network': 'नेटवर्क समस्या. पुन्हा प्रयत्न करा.',
  },
};

export default translations;
